import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Stack, TextField, CommandBarButton, IconButton } from '@fluentui/react';
import Layout from '../layout/Layout';
import Message from './message/Message'; // Import the Message component
import DateDisplay from './date/DateDisplay'; // Import the DateDisplay component
import ExportComponent from './exportchat/ExportComponent';
import PostQueryRequest from '../../models/PostQueryRequest';
import IBotService from '../../interfaces/IBotService';
import IMessage from '../../models/IMessage';
import ChatsHistory from './ChatsHistory';
import { useLocation } from 'react-router-dom';
import SuggestionToolbar from './prompt/SuggestionToolbar';
import { ProcessingProgressIndicator } from './progress indicator/ProcessingProgressIndicator';
import BotStartMessage from '../../models/BotStartMessage';
import styles from './Chat.module.scss';
import { MSGraphUser } from '../../models/MSGraphUser';
import { chatboxStyles } from '../../styles/style';
import { MessageData } from '../../mappers/MessageData';
import MessageDataMapper from '../../mappers/MessageDataMapper';
import BotStartMessageMapper from '../../mappers/BotStartMessageMapper';
import { BotServiceFactory } from '../../services/BotServiceFactory';
import Suggestion from '../../models/Suggestion';
import { graphInstance } from '../../index';
import { ChatMode } from '../../enums/ChatMode';
import NewChat from './new chat/NewChat';
import Constants from '../../constants/Constansts';
import { IResponse } from '../../models/IResponse';
import { QueryResponse } from '../../models/QueryResponse';
import BotDisclaimer from './disclaimer/BotDisclaimer';
import { useHistory } from 'react-router-dom';

const ChatComponent = () => {
  const [state, setState] = useState();
  const history = useHistory();
  const exportRef = useRef<HTMLDivElement>(null);
  const [userProfile, setUserProfile] = useState<MSGraphUser>();
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [showChats, setShowChats] = useState(true);
  const [chatDate, setChatDate] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showProgressIndicator, setShowProgressIndicator] = useState(true);
  const location = useLocation();
  const [chat_mode,] = useState<ChatMode>(location.state as ChatMode);
  const [createClientRoot, setCreateClientRoot] = useState<boolean>(true);
  const [parentMessageId, setParentMessageId] = useState<string | undefined>('');
  const [conversationId, setConversationId] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  console.log("data location", chat_mode);
  const botService: IBotService = BotServiceFactory.getBotService(Constants.USE_MOCK_SERVICE);
  
  useEffect(() => {
    if (!location.state) {
      history.push("/");
    }
  }, [location.state]);

  useEffect(() => {
    try {
      if (userProfile) {
        // bot start message has dependency on user profile data
        fetchBotStartMessage();
      }
    } catch (error) {
      console.error('Error:', error);
      setState(() => {
        throw error;
      })
    }
  }, [userProfile]);

  useEffect(() => {
    try {
      const currentDate = new Date();
      const options: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      const formattedDate = currentDate.toLocaleDateString('en-US', options);
      setChatDate(formattedDate);
      const fetchUserProfile = async () => {
        const data = await graphInstance.getGraphUserProfile();
        setUserProfile(data);
      };
      fetchUserProfile();

    } catch (error) {
      console.error('Error:', error);
      setState(() => {
        throw error;
      })
    }
  }, []);


  const addUserDisplayNameInStartMessage = (startMessage: IMessage): IMessage => {

    let keyword = "there!";
    let displayName = userProfile?.givenName ?? "User";
    let message = startMessage.content.replace(keyword, displayName);
    let updatedMessage: IMessage = { ...startMessage, content: message }
    console.log(userProfile)
    return updatedMessage;
  }
  const fetchBotStartMessage = async () => {
    try {
      setShowProgressIndicator(true);


      const botStartMessage: BotStartMessage = await botService.getBotStartMessage(chat_mode);
      const startMessage: IMessage = BotStartMessageMapper.mapToIMessageFromBotStartMessage(botStartMessage, chat_mode);

      const responseAsMessage: MessageData = {
        message: addUserDisplayNameInStartMessage(startMessage),
        isUser: false
      };
      setSuggestions(botStartMessage.Suggestions);
      setMessages((prevMessages) => [...prevMessages, responseAsMessage]);
      setShowProgressIndicator(false);
    } catch (error: any) {
      console.error('Error:', error);
      let errorMessage: MessageData = MessageDataMapper.mapToMessageDataFromError(error, chat_mode);
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
      setShowProgressIndicator(false);
    }
  };


  const handleChatClick = async (conversationId: string) => {
    try {
      setConversationId(conversationId);
      const messageDataArray = await processConversations(conversationId);
      setParentMessageId(getLastParentMessageId(messageDataArray));
      setMessages(messageDataArray);
      setInputValue('');
      setShowProgressIndicator(false);
    }
    catch (error: any) {
      console.error('Error:', error);
      let errorMessage: MessageData = MessageDataMapper.mapToMessageDataFromError(error, chat_mode);
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
      setShowProgressIndicator(false);
    }
  };

  const handleError = useCallback((error: any) => {
    const errorMessage: MessageData = MessageDataMapper.mapToMessageDataFromError(error, chat_mode);

    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];

      // Avoid adding duplicate error messages
      if (lastMessage?.message?.content !== errorMessage.message.content) {
        return [...prevMessages, errorMessage];
      }

      return prevMessages; // No update if duplicate
    });
  }, [chat_mode]);


  const processConversations = async (conversationId: string): Promise<MessageData[]> => {
    const displayName = userProfile?.displayName ?? "User";
    let conversations: MessageData[] = [];

    try {
      const chatHistory: QueryResponse = await botService.getConversationHistory(conversationId, chat_mode);
      console.log("chatHistory", chatHistory);
      conversations = MessageDataMapper.mapToMessageDataFromHistoryQueryResponse(
        chatHistory,
        chat_mode,
        displayName
      );
    } catch (error: any) {

      console.error("Error fetching conversation history:", error);
      throw error;
    }

    return conversations;
  };


  function getLastParentMessageId(conversations: MessageData[]): string {
    const lastMessage = conversations.at(-1);
    return lastMessage?.message?.message_id ?? '';
  }

  const handleSendMessage = async (messageContent: string) => {
    try {
      setShowProgressIndicator(true);
      const displayName: string = userProfile?.displayName as string

      // Create PostQueryRequest
      const postQueryRequest: PostQueryRequest = {
        message_id: '', // Need to check purpose of this
        query: messageContent,
        conversation_id: conversationId,
        chat_mode: chat_mode,
        parent_message_id: createClientRoot ? Constants.CREATE_CLIENT_ROOT : parentMessageId!,
        timezone: Constants.TIMEZONE,
        timezone_offset: Constants.TIMEZONE_OFFSET,
        created: new Date(),
      };

      // Map post query to Message data
      const message_query: MessageData = MessageDataMapper.mapToMessageDataFromPostQueryRequest(postQueryRequest, displayName);
      setMessages([...messages, message_query]);
      setInputValue('');
      setCreateClientRoot(false);

      try {
        const responseMessage: IResponse = await botService.postQuery(postQueryRequest);

        // Map response to Message data
        const message_response: MessageData = MessageDataMapper.mapToMessageDataFromQueryResponse(responseMessage, chat_mode);
        console.log("responseMessage", responseMessage);
        setMessages((prevMessages) => [...prevMessages, message_response]);
        setConversationId(message_response.message.conversation_id as string);
        console.log("conversationId", conversationId);
        setParentMessageId(message_response.message.message_id);
      }
      catch (error: any) {
        let errorMessage: MessageData = MessageDataMapper.mapToMessageDataFromError(error, chat_mode);
        setMessages((prevMessages) => [...prevMessages, errorMessage]);
      }


      setShowProgressIndicator(false);
    }
    catch (error: any) {
      console.error('Error:', error);
      setState(() => {
        throw error;
      })
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim() !== '') {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const resetState = () => {
    console.log("Old parentMessageId -", parentMessageId);
    setCreateClientRoot(true);
    setParentMessageId('');
    setMessages([]);
    setSuggestions([]);
    setInputValue('');
    setShowChats(true);
    setShowProgressIndicator(true);
    fetchBotStartMessage();
    setConversationId('');
    console.log("New parentMessageId after new chat click -", parentMessageId);

  };

  return (
    <Layout>
      <Stack verticalFill className={styles.root} horizontal horizontalAlign="space-between" styles={{ root: { height: '88vh', width: '100%', background: '#ffffff', padding: 0 } }}>

        <Stack.Item styles={{ root: { margin: '0 20px', height: '100%', width: showChats ? '97%' : '80%', display: 'flex', justifyContent: 'center' } }}>
          <Stack styles={{ root: { height: '100%', flexGrow: 1, maxWidth: '1024px' } }}>
            <Stack styles={{ root: { position: 'sticky', top: 0, zIndex: 10, padding: '1px 0' } }}>
              <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="space-between">
                <Stack.Item align='center' grow>
                  <DateDisplay date={chatDate} />
                </Stack.Item>
                <Stack.Item align='end'>
                  <NewChat onClick={resetState} />
                </Stack.Item>
              </Stack>
            </Stack>
            <Stack.Item styles={{ root: { height: '90%', overflowY: 'auto', overflowX: 'auto', maxWidth: '100vw' } }}>
              <div ref={exportRef} id="content">
                <Stack tokens={{ childrenGap: 10 }}>
                  {messages.map((messageData, index) => (
                    <React.Fragment key={index + 'key'}>
                      <Message
                        {...messageData.message}
                      />
                    </React.Fragment>
                  ))}
                  <div ref={messagesEndRef} />
                </Stack>

              </div>
            </Stack.Item>
            <Stack.Item className={styles.suggestionToolbar}>
              {
                showProgressIndicator ? <ProcessingProgressIndicator /> :
                  <Stack horizontal wrap horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Stack.Item grow>
                      <SuggestionToolbar chatMode={chat_mode} onClick={handleSendMessage} suggestions={suggestions} />
                    </Stack.Item>
                    <Stack.Item align="end">
                      <ExportComponent />
                    </Stack.Item>
                  </Stack>
              }
            </Stack.Item>
            <Stack.Item>

              <div className={chatboxStyles.wrapper}>
                <TextField
                  multiline
                  rows={4}
                  resizable={false}
                  placeholder={chat_mode === ChatMode.Intranet ? "Ask a question": "Enter a message..."}
                  value={inputValue}
                  onChange={(e) => setInputValue((e.target as HTMLInputElement).value)}
                  onKeyDown={handleKeyPress}
                  maxLength={400}
                  errorMessage={inputValue.length > 400 ? "Character limit exceeded" : ""}
                  className={chatboxStyles.textField}
                />
                <div className={`${chatboxStyles.wrapper} ${chatboxStyles.disclaimerWrapper}`} >
                  <div>{inputValue.length}/400</div>
                  <div
                    className={chatboxStyles.disclaimer}>
                    <BotDisclaimer chatMode={chat_mode} />
                  </div>
                </div>

                <CommandBarButton
                  iconProps={{ iconName: 'Send' }}
                  onClick={() => handleSendMessage(inputValue)}
                  className={chatboxStyles.sendButton}
                  ariaLabel="Send"
                  disabled={showProgressIndicator || inputValue.trim().length === 0 || inputValue.length > 400}
                />
              </div>
            </Stack.Item>
          </Stack>
        </Stack.Item>

        <Stack.Item styles={{
          root: {
            width: showChats ? '3%' : '20%', paddingLeft: 10
          }
        }}>
          <IconButton
            iconProps={{ iconName: showChats ? 'OpenPane' : 'ClosePane' }}
            onClick={() => { setShowChats(!showChats); }}
            styles={{ root: { marginBottom: 10 } }}
          />
          {
            !showChats && <ChatsHistory onClick={(conversationId: string) => handleChatClick(conversationId)} chat_mode={chat_mode} handleError={handleError} conversationId={conversationId} />
          }
        </Stack.Item>

      </Stack>
    </Layout>
  );
};

export default ChatComponent;